import { useTranslation } from 'next-i18next'
import { withTranslateRoutes } from 'next-translate-routes'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import NProgress from 'nprogress'
import React, { ComponentType, useEffect } from 'react'
import { ThemeProvider } from 'styled-components'

import type { TCommonPageProps } from '@hozana/api/server/common'
import { dayjs } from '@hozana/dates'
import { detectLangFromPath } from '@hozana/intl/functions'
import { withI18n } from '@hozana/intl/withI18n'
import { useDispatch } from '@hozana/redux/hooks'
import { wrapper } from '@hozana/redux/initStore'
import { ScreenProvider } from '@hozana/screen/ScreenProvider'
import { Sentry } from '@hozana/tracking/sentry'

import theme from 'config/theme'
import { setIsHydrated } from 'general/actions'
import { DebuggerManager } from 'general/managers/debugger/DebuggerManager'
import { ErrorBoundaryManager } from 'general/managers/errorsBoundary/ErrorBoundaryManager'
import type { TLocale } from 'i18n/constants'
import { PAGE } from 'routes/constants'

import { MeProvider } from 'modules/user/hooks/MeProvider'

// Import CSS files so that they are available globally
// https://nextjs.org/docs/basic-features/built-in-css-support
import 'public/css/reset.css'
import 'public/css/sanitize.css'
import 'public/css/hozana-global.css'
import 'public/css/nprogress.css'

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

import { fetchI18nFallbackNamespaces } from '@hozana/api/actions'

import TermlyCMP, { ContentTermlyToggler } from 'general/components/TermlyCMP'

const WidgetContainer = dynamic(() => import('general/containers/WidgetContainer').then((m) => m.WidgetContainer))
const AppContainer = dynamic(() => import('general/containers/AppContainer').then((m) => m.AppContainer))

const progressStart = (_pathname: string, { shallow }: { shallow: boolean }) => {
  if (!shallow) {
    NProgress.start()
  }
}
const progressDone = (_pathname: string, { shallow }: { shallow: boolean }) => {
  if (!shallow) {
    NProgress.done()
  }
}

const getContainer = (pathname: PAGE): ComponentType<TNoMoreProps> => {
  switch (pathname) {
    case PAGE.INTENTIONS_WIDGET:
    case PAGE.INTENTIONS_WIDGET_FORM:
    case PAGE.COMMUNITY_WIDGET:
      return WidgetContainer
    default:
      return AppContainer
  }
}
const originalFetch = global.fetch
global.fetch = async (...args) => {
  process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0'
  console.log(`🌐 Fetching: ${CONF_KEYS.API_URL}`, args[0])
  try {
    const res = await originalFetch(...args)
    if (res.status === 404) {
      console.log('❌ Response:', res.status, res.url)
      return res
    }
    console.log('✅ Response:', res.status, res.url)
    return res
  } catch (err) {
    console.log('-----')
    console.log(args)
    console.error('❌ Global fetch error:', err, args)
    throw err
  }
}

/**
 * ### App ###
 */
const App: TFCC<AppProps<TCommonPageProps>> = ({
  Component: PageComponent,
  pageProps: {
    statusCode,
    userAgent = __CLIENT__ ? (navigator && (navigator.userAgent || navigator.vendor)) || window.opera : undefined,
    locale: serverSideLocale,
    ...pageProps
  } = {},
  router,
}) => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()

  i18n.on('missingKey', (lngs, namespace, key) => {
    dispatch(fetchI18nFallbackNamespaces())
    const message = `Missing translation in ${router.pathname} page in ${lngs.join(', ')} for ${namespace}:${key}.`
    if (CONF_KEYS.ENV === 'prod' && Math.random() < 0.0001) {
      Sentry.captureMessage('Missing translation', 'error', { lngs, namespace, key })
    } else if (CONF_KEYS.ENV === 'test') {
      throw new Error(message)
    } else {
      console.error(message)
    }
  })

  // serverSideLocale can be different than router.locale for widgets
  const locale = (serverSideLocale || router.locale || detectLangFromPath(router.asPath)) as TLocale

  useEffect(() => {
    router.events.on('routeChangeStart', progressStart)
    router.events.on('routeChangeComplete', progressDone)
    router.events.on('routeChangeError', progressDone)

    return () => {
      router.events.off('routeChangeStart', progressStart)
      router.events.off('routeChangeComplete', progressDone)
      router.events.off('routeChangeError', progressDone)
    }
  }, [router.events])

  useEffect(() => {
    dispatch(setIsHydrated())
  }, [dispatch])

  // change locale to format hour
  dayjs.locale(i18n.language || locale)

  const Container = getContainer(router.pathname as PAGE)

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <ThemeProvider theme={theme}>
        <GoogleReCaptchaProvider
          container={{ parameters: { badge: 'inline' } }}
          scriptProps={{
            async: false,
            defer: false,
            appendTo: 'head',
            nonce: undefined,
          }}
          reCaptchaKey={CONF_KEYS.RECAPTCHA_API_V3_KEY}
        >
          <ScreenProvider userAgent={userAgent}>
            <ErrorBoundaryManager>
              <MeProvider locale={locale}>
                <ErrorBoundaryManager showHeader>
                  <DebuggerManager />
                  <Container>
                    <ContentTermlyToggler>
                      <TermlyCMP />
                    </ContentTermlyToggler>
                    <PageComponent {...{ ...pageProps, router, statusCode }} />
                  </Container>
                </ErrorBoundaryManager>
              </MeProvider>
            </ErrorBoundaryManager>
          </ScreenProvider>
        </GoogleReCaptchaProvider>
      </ThemeProvider>
    </>
  )
}

export default withTranslateRoutes(JSON.parse(`{"defaultLocale":"fr","locales":["fr","en","es","pt","it","pl"],"routesTree":{"name":"","paths":{"default":""},"children":[{"name":"communities-list","paths":{"default":"communities-list","fr":"liste-communautes","es":"lista-de-comunidades","pt":"lista-de-comunidades"}},{"name":"connect","paths":{"default":"connect","fr":"connexion","es":"conexion","pt":"conexao"}},{"name":"index","paths":{"default":"index"}},{"name":"new-password","paths":{"default":"new-password","fr":"nouveau-mot-de-passe","es":"nueva-contrasena","pt":"nova-senha"}},{"name":"auth","paths":{"default":"auth"},"children":[{"name":"social","paths":{"default":"social"},"children":[{"name":"callback","paths":{"default":"callback"}}]}]},{"name":"community","paths":{"default":"community","fr":"communaute","es":"comunidad","pt":"comunidade"},"children":[{"name":"[communityId]","paths":{"default":".(\\\\d+)"},"children":[{"name":"noslug","paths":{"default":":communityId(\\\\d+)"}},{"name":"[communitySlug]","paths":{"default":":communityId(\\\\d+)-:communitySlug"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"landing","paths":{"default":"landing"}},{"name":"publications","paths":{"default":"publications","es":"publicaciones","pt":"publicacoes"}}]}]}]},{"name":"intention","paths":{"default":"intention","es":"intencion","pt":"intencao"},"children":[{"name":"[intentionId]","paths":{"default":".(\\\\d+)"},"children":[{"name":"[shareToken]","paths":{"default":":intentionId(\\\\d+)-:shareToken"}}]}]},{"name":"intentions","paths":{"default":"intentions","es":"intenciones","pt":"intencoes"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[landingPageSlug]","paths":{"default":":landingPageSlug"}}]},{"name":"my-account","paths":{"default":"my-account","fr":"mon-compte","es":"mi-cuenta","pt":"minha-conta"},"children":[{"name":"parameters","paths":{"default":"parameters","fr":"parametres","es":"configuraciones","pt":"configuracoes"}},{"name":"messaging","paths":{"default":"messaging","fr":"messagerie","es":"mensajeria","pt":"messaging"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[conversationId]","paths":{"default":":conversationId"}}]}]},{"name":"widget","paths":{"default":"widget"},"children":[{"name":"communaute","paths":{"default":"communaute"},"children":[{"name":"[communityId]","paths":{"default":":communityId"}}]},{"name":"intentions","paths":{"default":"intentions"},"children":[{"name":"[widgetId]","paths":{"default":":widgetId"},"children":[{"name":"form","paths":{"default":"form"}},{"name":"index","paths":{"default":"index"}}]}]}]}]}}`), wrapper.withRedux(withI18n(App)))
